import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, ShieldCheck, CreditCard, FileText, LogOut, Sparkles, Chrome, HelpCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";

export default function Sidebar({ onOpenTos, onOpenPrivacy, onOpenContact, onOpenChrome }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const items = [
    { to: "/dashboard", label: "Workspace", icon: LayoutGrid, testid: "nav-workspace" },
  ];
  if (user?.is_admin) {
    items.push({ to: "/admin", label: "Admin Panel", icon: ShieldCheck, testid: "nav-admin" });
  }

  return (
    <aside
      className="hidden lg:flex flex-col w-[260px] shrink-0 h-screen sticky top-0 bg-charcoal border-r border-border"
      data-testid="sidebar"
    >
      <div className="px-5 py-5 border-b border-border">
        <Logo size={34} />
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 sr-scroll overflow-y-auto">
        {items.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border transition-colors ${
                isActive
                  ? "bg-teal/10 text-white border-teal/40"
                  : "border-transparent text-white/70 hover:text-white hover:bg-white/[0.03] hover:border-border"
              }`
            }
          >
            <Icon className="h-4 w-4" />
            <span className="font-medium tracking-tight">{label}</span>
          </NavLink>
        ))}

        <button
          data-testid="nav-billing"
          onClick={() => nav("/dashboard?action=upgrade")}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border border-transparent text-white/70 hover:text-white hover:bg-white/[0.03] hover:border-border"
        >
          <CreditCard className="h-4 w-4" />
          <span className="font-medium tracking-tight">Billing</span>
        </button>

        {onOpenChrome && (
          <button
            data-testid="nav-chrome-ext"
            onClick={onOpenChrome}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border border-transparent text-white/70 hover:text-white hover:bg-white/[0.03] hover:border-border"
          >
            <Chrome className="h-4 w-4" />
            <span className="font-medium tracking-tight">Chrome Extension</span>
          </button>
        )}

        {onOpenContact && (
          <button
            data-testid="nav-help"
            onClick={onOpenContact}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border border-transparent text-white/70 hover:text-white hover:bg-white/[0.03] hover:border-border"
          >
            <HelpCircle className="h-4 w-4" />
            <span className="font-medium tracking-tight">Help / Contact</span>
          </button>
        )}

        <div className="pt-5 mt-3 border-t border-border space-y-1">
          <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] font-mono text-white/40">Legal</p>
          <button
            data-testid="nav-tos"
            onClick={onOpenTos}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-sm text-white/60 hover:text-white hover:bg-white/[0.03]"
          >
            <FileText className="h-4 w-4" />
            Terms of Service
          </button>
          <button
            data-testid="nav-privacy"
            onClick={onOpenPrivacy}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-sm text-white/60 hover:text-white hover:bg-white/[0.03]"
          >
            <FileText className="h-4 w-4" />
            Privacy Policy
          </button>
        </div>
      </nav>

      {user && (
        <div className="px-4 py-4 border-t border-border" data-testid="sidebar-user">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-teal/20 border border-teal/40 grid place-items-center text-sm font-bold text-white">
              {(user.name || user.email)[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{user.name}</div>
              <div className="text-[11px] text-white/50 truncate font-mono">{user.email}</div>
            </div>
          </div>
          {user.funnel_state === "lifetime" && (
            <div className="mt-3 sr-pill text-teal">
              <Sparkles className="h-3 w-3" /> Lifetime Access
            </div>
          )}
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="mt-3 w-full justify-start text-white/60 hover:text-white hover:bg-white/[0.03] h-8 px-2"
          >
            <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
          </Button>
        </div>
      )}
    </aside>
  );
}
